import React, { useEffect, useRef } from "react"
import { getSubmissionStatusAsString, formatOfferValue } from "../utils/Helpers"
import { rebateDetailModalStyles as styles, CIRCLE_SIZE } from "./styled"
import { SUBMISSION_STATUS_ID } from "../utils/SubmissionStatusID"

// Mirrors the legacy RedeemButtonComponent gating — only these offers route
// users through `submission.redemptionLink` for redemption.
const REWARD_OFFER_ARRAY = [
  Number.parseInt(process.env.REACT_APP_YT_BASE_GROUPA_OFFER_ID),
  Number.parseInt(process.env.REACT_APP_YT_BASE_GROUPB_OFFER_ID),
  Number.parseInt(process.env.REACT_APP_YT_BASE_GROUPC_OFFER_ID),
  268504,
  268509,
]

const STATUS_TO_STEP = {
  Received: 0,
  Pending:  1,
  Approved: 2,
  Denied:   2,
}

const STEPS = ["Received", "Reviewing", "Approved"]

const MODAL_CONTENT = {
  Received: {
    title:   "Submission received",
    message: "We've received your submission. Please allow for 24 hours for the status to update.",
  },
  Pending: {
    title:   "Pending validation",
    message: "We're reviewing your submission. This may take a few business days.",
  },
  Approved: {
    title:   "Submission approved",
    message: "Your submission has been approved!",
  },
  Denied: {
    title:   "Submission denied",
    message: "Your submission was not approved.",
  },
}

const ArrowIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M4 11h14M12 6l5 5-5 5" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SearchIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="8.5" cy="8.5" r="5.5" stroke={color} strokeWidth="2" />
    <path d="M13 13l4 4" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const CheckIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M4 11.5l5.5 5.5 8.5-10" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const NoIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="8" stroke={color} strokeWidth="2.2" />
    <path d="M5.3 5.3l11.4 11.4" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="10" cy="10" r="10" fill="#28a745" />
    <path d="M5.5 10.5L8.5 13.5L14.5 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)


const STEP_ICONS = [ArrowIcon, SearchIcon, CheckIcon]

const formatDate = (dateStr) => {
  if (!dateStr) return "—"
  const d = new Date(dateStr)
  if (isNaN(d)) return "—"
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}


const getRewardUrl = (submission) => {
  if (submission.debitCard?.virtualPayClaimCode) {
    return submission.debitCard.virtualPayClaimCode
  }
  if (
    submission.statusId === SUBMISSION_STATUS_ID.SENT &&
    submission.error === 0 &&
    submission.valid === true &&
    submission.virtualPayClaimCode &&
    submission.virtualPayClaimCode !== "EMPTY"
  ) {
    return submission.virtualPayClaimCode
  }
  return null
}

export const RebateDetailModal = ({ submission, onClose }) => {
  const status = getSubmissionStatusAsString(submission)
  const activeStep = STATUS_TO_STEP[status] ?? 0
  const { title, message } = MODAL_CONTENT[status] || MODAL_CONTENT.Received
  const rewardUrl = getRewardUrl(submission)
  // rewardType strings come from the API; matching is case-insensitive so common
  // variants like "Bill Credit", "BILL_CREDIT", "Recurring Bill Credit" all work.
  const rewardType = submission.offer?.rewardType || ""
  const isApproved = status === "Approved"
  // Premium Disbursed Offer — show the raw ECode as a promo code only when
  // there's no redemptionLink (i.e., the API didn't return an ImportedItemDesc
  // or ImportJobDesc). If a redemptionLink is present, the user is redirected
  // through that path instead.
  const ecode = submission.ecode || submission.eCode
  const hasEcode = !!(ecode && String(ecode).trim())
  const hasRedemptionLink = !!(submission.redemptionLink && String(submission.redemptionLink).trim())
  const isApprovedPromoCode = isApproved && hasEcode && !hasRedemptionLink
  const isRecurringBillCredit = isApproved && !isApprovedPromoCode && /recurring/i.test(rewardType)
  const isBillCredit = isApproved && !isApprovedPromoCode && !isRecurringBillCredit && /bill\s*credit/i.test(rewardType)
  const offerValueDisplay = formatOfferValue(submission.offer?.offerValue)
  const showRewardButton = !!rewardUrl && !isBillCredit && !isRecurringBillCredit && !isApprovedPromoCode
  // Redemption-link button shows when EITHER:
  //  (a) Premium Disbursed Offer: approved + ECode present + redemptionLink
  //      present (Status=3, Valid=1 covered by `isApproved`), or
  //  (b) Legacy fallback: `submission.valid` AND offerId in the allow-list —
  //      preserves current behavior for YouTube/268504/268509 offers that
  //      don't carry an ECode yet.
  const showRedemptionLinkButton =
    (isApproved && hasEcode && hasRedemptionLink) ||
    (!!submission.valid && REWARD_OFFER_ARRAY.includes(submission.offerId))

  const displayMessage =
    isApprovedPromoCode
      ? `Your rebate was approved! Here is your promo code: ${ecode}`
      : isRecurringBillCredit
        ? "Your rebate was approved! Your recurring credit will appear on your next bill."
        : isBillCredit
          ? `Your rebate was approved! Your ${offerValueDisplay} credit will appear on your next bill.`
          : (status === "Pending" || status === "Partial") && submission.redeemDays
            ? `We're reviewing your submission. Please allow ${submission.redeemDays} for a decision.`
            : message

  const deniedErrors =
    status === "Denied" && submission.offerErrors?.length > 0
      ? submission.offerErrors.map((err) => err.description).filter(Boolean)
      : []
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768

  const sheetRef = useRef(null)

  const progressWidth =
    activeStep === 0 ? 0
    : activeStep === 1 ? `calc(50% - ${CIRCLE_SIZE / 2}px)`
    : `calc(100% - ${CIRCLE_SIZE}px)`

  useEffect(() => {
    document.body.style.overflow = "hidden"

    const focusable = sheetRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const first = focusable ? focusable[0] : null
    const last = focusable ? focusable[focusable.length - 1] : null
    if (first) first.focus()

    const handleKeyDown = (e) => {
      if (e.key === "Escape") { onClose(); return }
      if (e.key !== "Tab" || !focusable?.length) return
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [onClose])

  return (
    <div
      style={{ ...styles.overlay, alignItems: isDesktop ? "center" : "flex-end" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={sheetRef}
        style={{ ...styles.sheet, borderRadius: isDesktop ? 16 : "16px 16px 0 0" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close rebate details">
          &#10005;
        </button>

        <h2 style={styles.title}>{title}</h2>

        {/* Pizza tracker */}
        <div style={styles.tracker}>
          <div style={styles.trackerLineBg} />
          <div style={{ ...styles.trackerLineProgress, width: progressWidth }} />
          {STEPS.map((label, i) => {
            const isDeniedFinal = i === 2 && status === "Denied"
            const Icon = isDeniedFinal ? NoIcon : STEP_ICONS[i]
            const stepLabel = isDeniedFinal ? "Denied" : label
            const done = i <= activeStep
            return (
              <div key={label} style={styles.step}>
                <div style={{ ...styles.stepCircle, background: done ? "#1a1a1a" : "#e9e9e9" }}>
                  <Icon color={done ? "#fff" : "#aaa"} />
                </div>
                <span style={{ ...styles.stepLabel, color: done ? "#111" : "#999", fontWeight: done ? 600 : 400 }}>
                  {stepLabel}
                </span>
              </div>
            )
          })}
        </div>

        {!(isApproved && (isBillCredit || isRecurringBillCredit || isApprovedPromoCode)) && (
          <p style={styles.message}>{displayMessage}</p>
        )}

        {deniedErrors.length > 0 && (
          <>
            <ul style={styles.errorList}>
              {deniedErrors.map((desc, idx) => (
                <li key={idx} style={styles.errorItem}>{desc}</li>
              ))}
            </ul>
            <p style={styles.supportText}>
              <a
                style={styles.supportLink}
                href="https://promotions.t-mobile.com/faq"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit rebate support
              </a>
              {" "}if you need more help.
            </p>
          </>
        )}

        <div style={styles.details}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Promotion name</span>
            <span style={styles.detailValue}>{submission.offer?.description || "—"}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Promotion value</span>
            <span style={styles.detailValue}>{formatOfferValue(submission.offer?.offerValue)}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Submission date</span>
            <span style={styles.detailValue}>{formatDate(submission.stampDate)}</span>
          </div>
            <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Tracking ID</span>
            <span style={styles.detailValue}>{submission.trackingId || "—"}</span>
          </div>
        </div>

        {showRewardButton && (
          <button
            style={styles.rewardBtn}
            onClick={() => window.open(rewardUrl, "_blank", "noopener,noreferrer")}
          >
            Get your reward
          </button>
        )}

        {showRedemptionLinkButton && (
          <button
            style={styles.rewardBtn}
            onClick={() => window.open(submission.redemptionLink, "_blank", "noopener,noreferrer")}
          >
            Click Here For Your Reward
          </button>
        )}

        {isApproved && (isBillCredit || isRecurringBillCredit || isApprovedPromoCode) && (
          <div style={styles.approvedCallout}>
            <CheckCircleIcon />
            <span>{displayMessage}</span>
          </div>
        )}
      </div>
    </div>
  )
}

